import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { formatDateTime, statusLabel } from "@/lib/helpers";
import { getStatusSemantic } from "@/lib/statusSemantics";
import { cn } from "@/lib/utils";
import { Wrench, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const IssueCard = ({ issue, isStaff, onResolve }) => {
  const priority = getStatusSemantic(issue.priority);
  const PriorityIcon = priority.icon;
  return (
  <div className="rounded-[var(--radius)] border bg-card p-4 space-y-2 hover:shadow-md transition-shadow" data-testid="vehicle-issue-card">
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-medium">{issue.vehicle_name} <span className="text-muted-foreground font-mono-ui text-xs">({issue.license_plate})</span></p>
      <Badge variant="outline" className={cn("border font-medium gap-1.5", priority.badge)} data-testid="vehicle-issue-priority-badge" aria-label={`Priority: ${statusLabel(issue.priority)}`}>
        <PriorityIcon className="h-3.5 w-3.5" aria-hidden="true" /> {statusLabel(issue.priority)}
      </Badge>
    </div>
    <p className="text-sm text-muted-foreground">{issue.description}</p>
    {issue.photos?.length > 0 && (
      <div className="flex gap-2 flex-wrap">
        {issue.photos.map((p) => (
          <a key={p} href={`${process.env.REACT_APP_BACKEND_URL}${p}`} target="_blank" rel="noreferrer">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${p}`} alt="issue" className="h-16 w-16 rounded object-cover border" />
          </a>
        ))}
      </div>
    )}
    <p className="text-xs text-muted-foreground">Reported by {issue.reported_by_name} &middot; {formatDateTime(issue.created_at)}</p>
    {issue.status === "open" && isStaff && (
      <Button size="sm" variant="outline" onClick={() => onResolve(issue.id)} data-testid="resolve-vehicle-issue-button">
        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Resolved
      </Button>
    )}
    {issue.status === "resolved" && (
      <p className="text-xs text-emerald-700">Resolved by {issue.resolved_by}</p>
    )}
  </div>
  );
};

export default function VehicleIssues() {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "office_staff";
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/vehicle-issues");
      setIssues(res.data);
    } catch {
      toast.error("Could not load vehicle issues");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id) => {
    try {
      await api.put(`/vehicle-issues/${id}`, { status: "resolved" });
      toast.success("Issue marked as resolved");
      load();
    } catch {
      toast.error("Could not resolve issue");
    }
  };

  const openIssues = issues.filter((i) => i.status === "open");
  const resolvedIssues = issues.filter((i) => i.status === "resolved");

  if (loading) return <Skeleton className="h-64 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-6" data-testid="vehicle-issues-page">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3" data-testid="vehicle-issues-column-new">
          <h2 className="text-lg font-semibold font-heading flex items-center gap-2">
            <Wrench className="h-5 w-5 text-destructive" /> Open Issues ({openIssues.length})
          </h2>
          {openIssues.length === 0 ? (
            <div className="rounded-[var(--radius)] border bg-card p-8 text-center text-sm text-muted-foreground">
              Fleet is healthy — every vehicle is operating normally with no open issues.
            </div>
          ) : (
            <div className="space-y-3">
              {openIssues.map((i) => <IssueCard key={i.id} issue={i} isStaff={isStaff} onResolve={resolve} />)}
            </div>
          )}
        </div>
        <div className="space-y-3" data-testid="vehicle-issues-column-resolved">
          <h2 className="text-lg font-semibold font-heading flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Resolved ({resolvedIssues.length})
          </h2>
          {resolvedIssues.length === 0 ? (
            <div className="rounded-[var(--radius)] border bg-card p-8 text-center text-sm text-muted-foreground">No resolved issues yet.</div>
          ) : (
            <div className="space-y-3">
              {resolvedIssues.map((i) => <IssueCard key={i.id} issue={i} isStaff={isStaff} onResolve={resolve} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
